import { Component, inject, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { TooltipModule } from 'primeng/tooltip';
import { Router } from '@angular/router';
import { RadioButtonModule } from 'primeng/radiobutton';
import { MessageModule } from 'primeng/message';
import { InputTextModule } from 'primeng/inputtext';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { ODataServiceFactory } from 'angular-odata';
import {
  CoatingLine,
  CustomerOrganisation,
  License,
  ProductionSite,
} from '@workspace-app/odatabackend/QualicoatBackendModel';
import { CustomerDataService } from './services/customer-data.service';
import {
  CreateLicenseDto,
  LineExtensionDto,
  LicenseNumberDataService,
  NextLicenseNumberResponse,
} from './services/license-number-data.service';
import { LineExtensionsSelectorComponent } from './components/line-extensions-selector/line-extensions-selector.component';
import {
  AutocompleteComponent,
  CalendarComponent,
  PageComponent,
  SelectComponent,
  SelectOption,
  ToastService,
  toUtcDateString,
} from '../../../../shared';

// License type constants matching backend
const LICENSE_TYPES = {
  COATER: 'Coater License',
  POWDER_SUPPLIER: 'Powder Supplier License',
  DECORATOR: 'Decorator License',
  FILM_SUPPLIER: 'Film Supplier License',
};

@Component({
  selector: 'app-create-license',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    PageComponent,
    ReactiveFormsModule,
    TooltipModule,
    SelectComponent,
    AutocompleteComponent,
    CalendarComponent,
    RadioButtonModule,
    MessageModule,
    InputTextModule,
    InputGroupModule,
    InputGroupAddonModule,
    LineExtensionsSelectorComponent,
  ],
  templateUrl: './create-license.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./create-license.component.scss'],
})
export class CreateLicenseComponent implements OnInit {
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private customerDataService = inject(CustomerDataService);
  private licenseNumberDataService = inject(LicenseNumberDataService);
  private toastService = inject(ToastService);
  private odataFactory = inject(ODataServiceFactory);

  // Form state
  organisations: CustomerOrganisation[] = [];
  productionSites: ProductionSite[] = [];
  productionSiteOptions: SelectOption[] = [];
  coatingLines: CoatingLine[] = [];
  lineExtensions: LineExtensionDto[] = [];

  // Next license number info
  nextLicenseNumber: NextLicenseNumberResponse | null = null;

  // License number validation state
  licenseNumberValid = signal<boolean | null>(null);
  licenseNumberError = signal<string | null>(null);
  formattedLicenseNumber = signal<string>('');
  checkingNumber = signal<boolean>(false);

  // Granting mode: auto-detected when coater license has no number and no issue date
  isGrantingMode = signal<boolean>(false);

  // Active licenses warning
  activeLicenses: License[] = [];
  showActiveLicensesWarning = false;

  // Year options
  currentYear = new Date().getFullYear();
  yearOptions: SelectOption[] = [
    { label: `This Year (${this.currentYear})`, value: 'this' },
    { label: `Next Year (${this.currentYear + 1})`, value: 'next' },
  ];

  // License type options
  licenseTypeOptions: SelectOption[] = [
    { label: 'Coater License', value: LICENSE_TYPES.COATER },
    { label: 'Powder Supplier License', value: LICENSE_TYPES.POWDER_SUPPLIER },
    { label: 'Decorator License', value: LICENSE_TYPES.DECORATOR },
    { label: 'Film Supplier License', value: LICENSE_TYPES.FILM_SUPPLIER },
  ];

  // Computed properties
  get isCoaterLicense(): boolean {
    return this.licenseForm.get('licenseType')?.value === LICENSE_TYPES.COATER;
  }

  get isDecoratorLicense(): boolean {
    return this.licenseForm.get('licenseType')?.value === LICENSE_TYPES.DECORATOR;
  }

  get licensePrefix(): string {
    // For decorator licenses, use country code from selected production site
    if (this.nextLicenseNumber?.usesDynamicPrefix) {
      const selectedSiteId = this.licenseForm.get('productionSiteId')?.value;
      if (selectedSiteId) {
        const site = this.productionSites.find((ps) => ps.id === selectedSiteId);
        if (site?.countryCode) {
          return site.countryCode;
        }
      }
      return 'XX'; // Placeholder for dynamic prefix
    }
    return this.nextLicenseNumber?.prefix || '';
  }

  get hasSelectedProductionSite(): boolean {
    return !!this.licenseForm.get('productionSiteId')?.value;
  }

  licenseForm = this.fb.group({
    licenseType: ['', Validators.required],
    licenseNumber: [null as number | null, [Validators.required, Validators.min(1)]],
    organisation: ['', Validators.required], // For autocomplete display
    organisationId: ['', Validators.required],
    productionSiteId: [''],
    certificationYear: ['next', Validators.required],
    issueDate: [new Date(this.currentYear + 1, 0, 1) as Date | null, Validators.required], // January 1st next year
    expiryDate: [new Date(this.currentYear + 1, 11, 31) as Date | null, Validators.required], // End of next year (LOCAL - normalized to UTC when sending to API)
  });

  ngOnInit(): void {
    // Watch for license type changes
    this.licenseForm.get('licenseType')?.valueChanges.subscribe((licenseType) => {
      if (licenseType) {
        this.fetchNextLicenseNumber(licenseType);
        this.updateFieldValidators();
      }
    });

    // Watch for license number changes to reset validation state and update granting mode
    this.licenseForm.get('licenseNumber')?.valueChanges.subscribe(() => {
      this.licenseNumberValid.set(null);
      this.licenseNumberError.set(null);
      this.updateGrantingMode();
    });

    // Watch for certification year changes
    this.licenseForm.get('certificationYear')?.valueChanges.subscribe((year) => {
      this.updateDatesFromCertificationYear(year as string);
    });

    // Watch for issue date changes to update expiry date and granting mode
    // Expiry is always end of the certification year
    this.licenseForm.get('issueDate')?.valueChanges.subscribe((issueDate) => {
      if (issueDate) {
        const certYear = this.licenseForm.get('certificationYear')?.value;
        const year = certYear === 'this' ? this.currentYear : this.currentYear + 1;
        const expiryDate = new Date(year, 11, 31); // December 31st (LOCAL - normalized to UTC when sending to API)
        this.licenseForm.patchValue({ expiryDate }, { emitEvent: false });
      }
      this.updateGrantingMode();
    });

    // Watch for production site changes
    this.licenseForm.get('productionSiteId')?.valueChanges.subscribe((productionSiteId) => {
      if (productionSiteId) {
        this.loadCoatingLines(productionSiteId);
        this.checkExistingLicenses(productionSiteId);
      } else {
        this.coatingLines = [];
        this.lineExtensions = [];
        this.activeLicenses = [];
        this.showActiveLicensesWarning = false;
      }
    });
  }

  searchOrganisation(query: string) {
    this.customerDataService.searchByName(query).subscribe((res) => {
      if (res.entities) {
        this.organisations = res.entities;
      }
    });
  }

  organisationSelected(organisation: CustomerOrganisation) {
    this.licenseForm.patchValue({
      organisation: organisation.name,
      organisationId: organisation.id,
      productionSiteId: '',
    });

    // Load production sites for this organisation
    this.loadProductionSites(organisation.id);

    // Clear previous data
    this.coatingLines = [];
    this.lineExtensions = [];
    this.activeLicenses = [];
    this.showActiveLicensesWarning = false;
  }

  onLineExtensionsChange(extensions: LineExtensionDto[]) {
    this.lineExtensions = extensions;
  }

  onLicenseNumberBlur(): void {
    const licenseType = this.licenseForm.get('licenseType')?.value;
    const number = this.licenseForm.get('licenseNumber')?.value;

    if (!licenseType || !number || number <= 0) {
      return;
    }

    this.checkingNumber.set(true);
    this.licenseNumberDataService.checkNumberExists(licenseType, number).subscribe({
      next: (response) => {
        this.checkingNumber.set(false);
        this.formattedLicenseNumber.set(response.formattedNumber);
        if (response.exists) {
          this.licenseNumberValid.set(false);
          this.licenseNumberError.set(`License number ${response.formattedNumber} already exists`);
        } else {
          this.licenseNumberValid.set(true);
          this.licenseNumberError.set(null);
        }
      },
      error: () => {
        this.checkingNumber.set(false);
        this.licenseNumberValid.set(null);
        this.licenseNumberError.set(null);
      },
    });
  }

  createLicense() {
    if (this.licenseForm.invalid) {
      return;
    }

    const formValue = this.licenseForm.value;

    if (this.isGrantingMode()) {
      // Granting mode: send minimal data
      const dto: CreateLicenseDto = {
        licenseType: formValue.licenseType!,
        isGranting: true,
        productionSiteId: formValue.productionSiteId || undefined,
      };

      this.licenseNumberDataService.createLicense(dto).subscribe({
        next: () => {
          this.toastService.displaySuccessMessage('Granting license created successfully');
          this.router.navigate(['/certification/licenses']);
        },
        error: (err) => {
          const message = err.error?.message || 'Failed to create granting license';
          this.toastService.displayErrorMessage(message);
        },
      });
      return;
    }

    // Standard mode: validate license number
    if (this.licenseNumberValid() === false) {
      return;
    }

    const issueDate = formValue.issueDate as Date;
    const expiryDate = formValue.expiryDate as Date;

    const dto: CreateLicenseDto = {
      licenseType: formValue.licenseType!,
      number: formValue.licenseNumber ?? undefined,
      productionSiteId: formValue.productionSiteId || undefined,
      issueDate: toUtcDateString(issueDate)!,
      expiryDate: toUtcDateString(expiryDate)!,
      firstCertificationYear: formValue.certificationYear === 'this' ? this.currentYear : this.currentYear + 1,
      grantingNumber: 0,
      lineExtensions: this.isCoaterLicense ? this.lineExtensions : undefined,
    };

    this.licenseNumberDataService.createLicense(dto).subscribe({
      next: (license) => {
        this.toastService.displaySuccessMessage(
          `License ${license.completeLicenseNumber || license.number} created successfully`
        );
        this.router.navigate(['/certification/licenses']);
      },
      error: (err) => {
        const message = err.error?.message || 'Failed to create license';
        this.toastService.displayErrorMessage(message);
      },
    });
  }

  // Private methods

  private fetchNextLicenseNumber(licenseType: string) {
    this.licenseNumberDataService.getNextLicenseNumber(licenseType).subscribe({
      next: (response) => {
        this.nextLicenseNumber = response;
        this.licenseForm.patchValue({ licenseNumber: response.nextNumber });
        this.formattedLicenseNumber.set(response.formattedNumber);
        this.licenseNumberValid.set(true);
        this.licenseNumberError.set(null);
      },
      error: () => {
        this.nextLicenseNumber = null;
        this.formattedLicenseNumber.set('');
        this.licenseNumberValid.set(null);
      },
    });
  }

  private loadProductionSites(organisationId: string) {
    const service = this.odataFactory.entitySet<ProductionSite>('ProductionSites');
    service
      .entities()
      .query((q) => {
        q.filter(`customerId eq ${organisationId}`);
        q.orderBy('name');
      })
      .fetch()
      .subscribe((res) => {
        this.productionSites = res.entities || [];
        this.productionSiteOptions = this.productionSites.map((ps) => ({
          label: ps.name || 'Unnamed Site',
          value: ps.id,
        }));

        // Auto-select if only one production site
        if (this.productionSites.length === 1) {
          this.licenseForm.patchValue({ productionSiteId: this.productionSites[0].id });
        } else if (this.productionSites.length === 0 && (this.isCoaterLicense || this.isDecoratorLicense)) {
          this.toastService.displayErrorMessage(
            'This organisation has no production sites. Please add a production site first.'
          );
        }
      });
  }

  private loadCoatingLines(productionSiteId: string) {
    const service = this.odataFactory.entitySet<CoatingLine>('CoatingLines');
    service
      .entities()
      .query((q) => {
        q.filter(
          `productionSiteId eq ${productionSiteId} and IsArchived eq false and UsedForArchitecturalCoatingsOnAluminium eq true`
        );
        q.orderBy('identifier');
      })
      .fetch()
      .subscribe((res) => {
        this.coatingLines = res.entities || [];
      });
  }

  private checkExistingLicenses(productionSiteId: string) {
    const service = this.odataFactory.entitySet<License>('Licenses');
    // Filter for active licenses: not withdrawn, not expired, not archived
    // Valid/Expiring/Granting = withdrawalDate is null AND (expiryDate is null OR expiryDate > now)
    const now = new Date().toISOString();
    service
      .entities()
      .query((q) => {
        q.filter(
          `productionSiteId eq ${productionSiteId} and withdrawalDate eq null and isArchived eq false and (expiryDate eq null or expiryDate gt ${now})`
        );
      })
      .fetch()
      .subscribe((res) => {
        this.activeLicenses = res.entities || [];
        this.showActiveLicensesWarning = this.activeLicenses.length > 0;
      });
  }

  private updateFieldValidators() {
    const productionSiteControl = this.licenseForm.get('productionSiteId');
    const licenseNumberCtrl = this.licenseForm.get('licenseNumber');
    const issueDateCtrl = this.licenseForm.get('issueDate');
    const expiryDateCtrl = this.licenseForm.get('expiryDate');
    const certYearCtrl = this.licenseForm.get('certificationYear');

    // Production site is required for Coater and Decorator licenses
    if (this.isCoaterLicense || this.isDecoratorLicense) {
      productionSiteControl?.setValidators([Validators.required]);
    } else {
      productionSiteControl?.clearValidators();
    }
    productionSiteControl?.updateValueAndValidity();

    if (this.isCoaterLicense) {
      // For coater licenses, number and date fields are optional (granting mode supported)
      licenseNumberCtrl?.clearValidators();
      licenseNumberCtrl?.setValidators([Validators.min(1)]);
      issueDateCtrl?.clearValidators();
      expiryDateCtrl?.clearValidators();
      certYearCtrl?.clearValidators();
    } else {
      // For other license types, number and dates are required
      licenseNumberCtrl?.setValidators([Validators.required, Validators.min(1)]);
      issueDateCtrl?.setValidators([Validators.required]);
      expiryDateCtrl?.setValidators([Validators.required]);
      certYearCtrl?.setValidators([Validators.required]);
    }

    licenseNumberCtrl?.updateValueAndValidity();
    issueDateCtrl?.updateValueAndValidity();
    expiryDateCtrl?.updateValueAndValidity();
    certYearCtrl?.updateValueAndValidity();

    this.updateGrantingMode();
  }

  private updateGrantingMode() {
    const licenseNumber = this.licenseForm.get('licenseNumber')?.value;
    const isGranting = this.isCoaterLicense && !licenseNumber;
    this.isGrantingMode.set(isGranting);
  }

  private updateDatesFromCertificationYear(year: string) {
    let issueDate: Date;
    let expiryDate: Date;

    if (year === 'this') {
      issueDate = new Date();
      // Expiry is end of current year (December 31st, LOCAL - normalized to UTC when sending to API)
      expiryDate = new Date(this.currentYear, 11, 31);
    } else {
      issueDate = new Date(this.currentYear + 1, 0, 1); // January 1st next year
      // Expiry is end of next year (December 31st, LOCAL - normalized to UTC when sending to API)
      expiryDate = new Date(this.currentYear + 1, 11, 31);
    }

    this.licenseForm.patchValue(
      {
        issueDate,
        expiryDate,
      },
      { emitEvent: false }
    );
  }

  private addOneYear(date: Date): Date {
    const result = new Date(date);
    result.setFullYear(result.getFullYear() + 1);
    return result;
  }
}
